/**
 * ============================================================================
 * TRANSIENT: Appointment Config Page (appointment-config-page)
 * Type: poc
 * Amplitude key: none
 * End date: 2026-12-31
 * PR: #TODO_AFTER_PR_CREATION_appointment-config-page
 * ============================================================================
 */

import { Box, Stack, Typography } from '@evaneos/b2b-ui';
import PhoneOutlinedIcon from '@evaneos/b2b-ui/icons/PhoneOutlined';
import VideocamOutlinedIcon from '@evaneos/b2b-ui/icons/VideocamOutlined';
import { useIntl } from 'react-intl';

import type { ContactType } from '../../../resources/appointment/operations';

const CONTACT_OPTIONS: {
    id: ContactType;
    icon?: JSX.Element;
}[] = [
    {
        id: 'audio_request_manager',
        icon: <PhoneOutlinedIcon fontSize="small" />,
    },
    {
        id: 'visio_request_manager',
        icon: <VideocamOutlinedIcon fontSize="small" />,
    },
    {
        id: 'other',
    },
];

type Props = {
    value: ContactType;
    onChange: (contact: ContactType) => void;
};

export const AppointmentContactPartial = ({ value, onChange }: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    return (
        <Stack data-spec-id="agenda-section-contact" spacing={2}>
            <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, fontSize: 20, mb: 0.5 }}>
                    {formatMessage({ id: 'appointment.contact.title' })}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {formatMessage({ id: 'appointment.contact.description' })}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Stack spacing={1} sx={{ flex: 1 }}>
                    {CONTACT_OPTIONS.map((opt) => {
                        const selected = value === opt.id;
                        return (
                            <Box
                                key={opt.id}
                                data-spec-id={`contact-card-${opt.id}`}
                                className={selected ? 'contact-card--selected' : ''}
                                onClick={() => onChange(opt.id)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: 'background.paper',
                                    transition: 'border-color 0.15s',
                                    '&:hover': { borderColor: 'primary.main' },
                                }}
                            >
                                {/* Radio indicator */}
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        border: '2px solid',
                                        borderColor: selected ? 'primary.main' : 'divider',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}
                                >
                                    {selected && (
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                            }}
                                        />
                                    )}
                                </Box>

                                {opt.icon && (
                                    <Box
                                        sx={{
                                            color: selected ? 'primary.main' : 'text.secondary',
                                            display: 'flex',
                                        }}
                                    >
                                        {opt.icon}
                                    </Box>
                                )}

                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: selected ? 700 : 400,
                                        color: selected ? 'primary.main' : 'text.primary',
                                    }}
                                >
                                    {formatMessage({ id: `appointment.contact.option.${opt.id}` })}
                                </Typography>
                            </Box>
                        );
                    })}
                </Stack>

                {(value === 'audio_request_manager' || value === 'visio_request_manager') && (
                    <Box
                        data-spec-id="agenda-contact-right-panel"
                        sx={{
                            flex: 1,
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                        }}
                    >
                        {value === 'audio_request_manager' && (
                            <Typography variant="body2">
                                {formatMessage({ id: 'appointment.contact.audio_rm_info' })}
                            </Typography>
                        )}
                        {value === 'visio_request_manager' && (
                            <Typography variant="body2">
                                {formatMessage({ id: 'appointment.contact.visio_info' })}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Stack>
    );
};
